import React, { createContext, useState, useContext, useEffect } from 'react';
import axios from 'axios';
import { buildApiUrl } from '../config/apiConfig';

const AuthContext = createContext();

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [token, setToken] = useState(localStorage.getItem('token'));
  const [loading, setLoading] = useState(true);

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => {
    const controller = new AbortController();
    
    // CRITICAL: If returning from OAuth callback, skip the /me check.
    // AuthCallback will exchange the session_id and establish the session first.
    if (window.location.hash?.includes('session_id=')) {
      setLoading(false);
      return;
    }
    
    // Check if OAuth just completed (from AuthCallback)
    const oauthCompleted = sessionStorage.getItem('oauth_completed');
    if (oauthCompleted === 'true') {
      console.log('🔄 OAuth completed, checking session...');
      sessionStorage.removeItem('oauth_completed'); // Clear flag
      checkSession(controller.signal);
      return;
    }
    
    if (token) {
      fetchUser(controller.signal);
    } else {
      // Check if session_token cookie exists (for OAuth users)
      checkSession(controller.signal);
    }
    
    return () => controller.abort(); // Cleanup for React 19 StrictMode
  }, [token]);

  const fetchUser = async (signal) => {
    try {
      const response = await axios.get(buildApiUrl('/auth/me'), {
        headers: { Authorization: `Bearer ${token}` },
        signal, // Pass abort signal to axios
        timeout: 5000 // 5 second timeout
      });
      setUser(response.data);
    } catch (error) {
      // Ignore abort errors from cleanup
      if (error.name === 'CanceledError' || error.name === 'AbortError') {
        return;
      }
      
      // Handle CORS and network errors gracefully
      if (error.code === 'ERR_NETWORK' || error.message?.includes('CORS') || error.message?.includes('Network Error')) {
        console.warn('Network/CORS error during authentication - continuing in offline mode');
      } else {
        console.error('Error fetching user:', error);
      }
      
      // Only logout on non-network errors
      if (!error.code === 'ERR_NETWORK' && !error.message?.includes('CORS')) {
        logout();
      }
    } finally {
      // Only set loading false if not aborted
      if (!signal || !signal.aborted) {
        setLoading(false);
      }
    }
  };

  const checkSession = async (signal) => {
    try {
      // Check if session_token cookie exists via /auth/me endpoint
      const response = await axios.get(buildApiUrl('/auth/me'), {
        withCredentials: true, // Send cookies
        signal,
        timeout: 5000 // 5 second timeout
      });
      setUser(response.data);
    } catch (error) {
      // Ignore abort errors and 401 (not authenticated)
      if (error.name === 'CanceledError' || error.name === 'AbortError') {
        return;
      }
      
      // Handle CORS and network errors gracefully
      if (error.code === 'ERR_NETWORK' || error.message?.includes('CORS') || error.message?.includes('Network Error')) {
        console.warn('Network/CORS error during session check - continuing without session');
      }
      
      // No session found, that's okay - just continue
    } finally {
      if (!signal || !signal.aborted) {
        setLoading(false);
      }
    }
  };

  const login = async (email, password, captchaToken = null) => {
    const response = await axios.post(buildApiUrl('/auth/login'), { 
      email, 
      password,
      captcha_token: captchaToken
    });
    const { access_token, user: userData } = response.data;
    setToken(access_token);
    localStorage.setItem('token', access_token);
    setUser(userData);
    return userData;
  };

  const signup = async (email, password, username, full_name, captchaToken = null) => {
    const response = await axios.post(buildApiUrl('/auth/signup'), {
      email,
      password,
      username,
      full_name,
      captcha_token: captchaToken
    });
    // Auto login after signup
    return await login(email, password);
  };

  const logout = async () => {
    // Call logout endpoint to clear session cookie
    try {
      await axios.post(buildApiUrl('/auth/logout'), {}, { withCredentials: true });
    } catch (error) {
      console.error('Logout error:', error);
    }
    
    setToken(null);
    setUser(null);
    localStorage.removeItem('token');
  };

  const getAuthHeader = () => {
    return token ? { Authorization: `Bearer ${token}` } : {};
  };

  return (
    <AuthContext.Provider value={{ user, token, login, signup, logout, loading, getAuthHeader }}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within AuthProvider');
  }
  return context;
};
